/**
 * "A new version is ready" prompt.
 *
 * Why this exists: before it, a deploy could leave a user on the old app with
 * no way to know. The service worker claimed the page the moment it activated
 * while the page kept its already-loaded JavaScript, so the two came from
 * different builds — and a shipped fix looked like it had never deployed.
 *
 * It asks rather than reloading on its own. This is a work tool: an agent may
 * be forty fields into a contract, and silently reloading would take that work
 * with it. Staleness is recoverable in one click; lost entry is not.
 *
 * WHY "LATER" HAS TO REMEMBER.
 *
 * A dismissed worker stays in `waiting`, and workbox fires its `waiting`
 * event again on every registration — so before this, "Later" lasted until
 * the next page load and the prompt came back, over and over, for a version
 * the user had already declined. A button that says Later has to mean later.
 *
 * The dismissal is held for an hour, which is the same cadence the update
 * check below runs on: decline a version, and the next thing you are asked
 * about is the next hourly check, not the same version again on reload.
 */

import { useEffect, useState } from 'react'
import { registerSW } from 'virtual:pwa-register'
import styles from './UpdatePrompt.module.css'

const DISMISSED_KEY = 'reppr_update_dismissed_at'
const CHECK_INTERVAL_MS = 60 * 60 * 1000

/**
 * Whether the user has recently said Later.
 *
 * A browser that refuses localStorage (private window, blocked site data)
 * throws here. That means we do not know of a dismissal, so we ask — the
 * prompt still works, it is just not silenced. Nothing about the app's data
 * is being hidden; this only decides whether to show a button.
 */
function dismissedRecently(): boolean {
  try {
    const at = Number(window.localStorage.getItem(DISMISSED_KEY))
    return Number.isFinite(at) && Date.now() - at < CHECK_INTERVAL_MS
  } catch {
    return false
  }
}

function rememberDismissal(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, String(Date.now()))
  } catch {
    // Nothing to do: the prompt simply asks again next time.
  }
}

/**
 * Taking the update ends the dismissal. Otherwise someone who declined one
 * version and then accepted the next would hear nothing for the rest of the
 * hour, which on a busy deploy day is several versions.
 */
function forgetDismissal(): void {
  try {
    window.localStorage.removeItem(DISMISSED_KEY)
  } catch {
    // Same as above: worst case, the prompt is quiet a while longer.
  }
}

export default function UpdatePrompt() {
  const [waiting, setWaiting] = useState(false)
  const [update, setUpdate] = useState<(() => Promise<void>) | null>(null)

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null

    const updateSW = registerSW({
      onNeedRefresh() {
        if (dismissedRecently()) return
        setUpdate(() => () => updateSW(true))
        setWaiting(true)
      },
      // Checked hourly as well as on load, so a long-lived tab — the normal
      // case for a desk tool someone leaves open all day — still finds out.
      onRegisteredSW(_url, registration) {
        if (!registration) return
        timer = setInterval(() => { registration.update().catch(() => {}) }, CHECK_INTERVAL_MS)
      },
    })

    return () => { if (timer) clearInterval(timer) }
  }, [])

  function later() {
    rememberDismissal()
    setWaiting(false)
  }

  if (!waiting) return null

  return (
    <div className={styles.bar} role="status">
      <span className={styles.text}>A new version of Reppr is ready.</span>
      <button className={styles.reload} onClick={() => { forgetDismissal(); update?.() }}>
        Reload
      </button>
      <button className={styles.later} onClick={later} aria-label="Dismiss">
        Later
      </button>
    </div>
  )
}
